import React from 'react'

/**
 * Dynamic TableComponent
 * @param {Array} columns - Array of column definitions: { key, label, width, thClass, tdClass }
 * @param {Array} data - Array of row objects
 * @param {Object} styleProps - Table style props (container, thead, tbody, etc)
 * @param {Function} renderCell - Optional custom cell renderer (row, col, value) => ReactNode
 */
const TableComponent = ({
  columns = [],
  data = [],
  styleProps = {},
  renderCell,
  headerCellStyle = {}, // { height, paddingY, paddingX, bg, borderB, borderR }
  bodyCellStyle = {},   // { height, paddingY, paddingX, borderB, borderR }
  containerBorder,
}) => {
  const {
    container = '',
    thead = '',
    tbody = '',
    tr = '',
    th = '',
    td = '',
    table = '',
    scroll = '',
  } = styleProps

  // Default border, radius, and shadow for table container
  const defaultContainerBorder = 'border border-[#E5E7EB] rounded-[10px] shadow-[0px_1px_2px_0px_#0000000D]';
  const borderClass = containerBorder !== undefined ? containerBorder : defaultContainerBorder;
  return (
    <div className={container ? `${container} ${borderClass}` : `w-full overflow-x-auto ${borderClass}`}>
      <table className={table ? `${table} table-fixed` : "w-full table-fixed"} style={{ borderCollapse: 'collapse' }}>
        <thead className={thead}>
          <tr className={tr}>
            {columns.map((col, colIdx) => {
              // Dynamic header cell styles
              const cleanClass = col.thClass ? col.thClass.replace(/border\s+border-\[#E5E7EB\]/g, '') : (th || '')
              const isFirst = colIdx === 0;
              const isLast = colIdx === columns.length - 1;
              // Defaults
              const defaultHeader = {
                height: 'h-10',
                paddingY: 'py-3',
                paddingX: 'px-4',
                bg: 'bg-[#F9FAFB]',
                borderB: 'border-b border-[#E5E7EB]',
                borderR: !isLast ? 'border-r border-[#E5E7EB]' : '',
                rounded: isFirst ? 'rounded-tl-[10px]' : isLast ? 'rounded-tr-[10px]' : '',
              }
              // Merge with props
              const mergedHeader = {
                ...defaultHeader,
                ...headerCellStyle,
                borderR: headerCellStyle.borderR !== undefined ? headerCellStyle.borderR : defaultHeader.borderR,
                rounded: headerCellStyle.rounded !== undefined ? headerCellStyle.rounded : defaultHeader.rounded,
              }
              // Ensure fontWeight from headerCellStyle takes precedence
              // Add support for custom font color via headerCellStyle
              const tailwindHeaderClass = `${mergedHeader.height} ${mergedHeader.paddingY} ${mergedHeader.paddingX} ${mergedHeader.bg} ${mergedHeader.borderB} ${mergedHeader.borderR} ${mergedHeader.rounded} ${cleanClass} ${mergedHeader.fontWeight || ''} ${mergedHeader.fontColor || ''}`
              return (
                <th
                  key={col.key}
                  className={tailwindHeaderClass}
                  style={{ width: col.width || 'auto' }}
                >
                  {col.label}
                </th>
              )
            })}
          </tr>
        </thead>
      </table>
      {/* Border directly below thead for clear separation */}
      <div className="w-full border-b border-[#E5E7EB]" style={{ marginTop: '-1px' }}></div>
      <div
        className={scroll || "overflow-y-auto border-b border-[#E5E7EB]"}
        style={styleProps.maxBodyHeight ? { maxHeight: styleProps.maxBodyHeight, height: 'auto', overflowY: 'auto' } : {}}
      >
        <table className={table ? `${table} table-fixed border-b border-[#E5E7EB]` : "w-full table-fixed border-b border-[#E5E7EB]"} style={{ borderCollapse: 'collapse' }}>
          <tbody className={tbody}>
            {data.map((row, rowIdx) => (
              <tr key={rowIdx} className={tr}>
                {columns.map((col, colIdx) => {
                  // Dynamic body cell styles
                  const cleanClass = col.tdClass ? col.tdClass.replace(/border\s+border-\[#E5E7EB\]/g, '') : (td || '')
                  const isLast = colIdx === columns.length - 1
                  // Defaults
                  const defaultBody = {
                    // Remove fixed height, use min-h for a little padding
                    height: '',
                    minHeight: 'min-h-[40px]',
                    paddingY: 'py-[10px]',
                    paddingX: 'px-4',
                    borderB: 'border-b border-[#E5E7EB]',
                    borderR: !isLast ? 'border-r border-[#E5E7EB]' : '',
                  }
                  const mergedBody = {
                    ...defaultBody,
                    ...bodyCellStyle,
                    borderR: bodyCellStyle.borderR !== undefined ? bodyCellStyle.borderR : defaultBody.borderR,
                  }
                  const tailwindBodyClass = `${mergedBody.height} ${mergedBody.minHeight || ''} ${mergedBody.paddingY} ${mergedBody.paddingX} ${mergedBody.borderB} ${mergedBody.borderR} ${cleanClass}`
                  return (
                    <td
                      key={col.key}
                      className={tailwindBodyClass}
                      style={{ width: col.width || 'auto', ...(col.cellStyle || {}) }}
                    >
                      {renderCell ? renderCell(row, col, row[col.key], rowIdx, colIdx) : row[col.key]}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default TableComponent